import { Globe, AppWindow, Sparkles, Smartphone } from "lucide-react";

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Staffing", to: "/staffing" },
  { label: "Products", to: "/products" },
  { label: "About Us", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const PROCESS_STEPS = [
  {
    step: "01",
    title: "Understand Your Goals",
    desc: "We start with your business — your goals, your customers and the problem you want to solve.",
  },
  {
    step: "02",
    title: "Plan the Right Solution",
    desc: "Together we shape the right product: scope, features, technology and a clear roadmap.",
  },
  {
    step: "03",
    title: "Design & Build Your Product",
    desc: "Our team designs and engineers your product in focused iterations, with you involved throughout.",
  },
  {
    step: "04",
    title: "Launch & Grow",
    desc: "We launch, measure and refine — helping your product grow with your business.",
  },
];

export const SERVICES = [
  {
    slug: "website-development",
    icon: Globe,
    name: "Website Design & Development",
    shortDesc:
      "Modern, responsive and high-performing websites designed around your brand and business goals.",
    tagline: "Websites that work as hard as you do.",
    overview:
      "Your website is often the first product experience your customers have. We design and build fast, responsive, conversion-focused websites that present your brand with clarity and turn visitors into enquiries.",
    whatWeBuild: [
      "Corporate and brand websites",
      "Marketing and landing pages",
      "Product and SaaS marketing sites",
      "Content-managed websites",
      "E-commerce storefronts",
    ],
    capabilities: [
      { title: "Responsive by default", desc: "Every layout is designed mobile-first and tested across phones, tablets and desktops." },
      { title: "Performance-focused builds", desc: "Optimised assets, clean code and fast load times that support SEO and conversions." },
      { title: "SEO foundations", desc: "Semantic structure, metadata and technical SEO built in from the first commit." },
      { title: "Brand-led design", desc: "Interfaces designed around your identity — not off-the-shelf templates." },
      { title: "Easy content control", desc: "Update pages, copy and media without depending on a developer." },
      { title: "Analytics-ready", desc: "Measurement wired in so you can see what your website is doing for you." },
    ],
    useCases: [
      { title: "Launch a new business", desc: "A polished first website that makes a young company look established." },
      { title: "Rebrand an outdated site", desc: "Replace a dated website with a modern, credible digital presence." },
      { title: "Support a product launch", desc: "Campaign and landing pages built to convert launch traffic." },
      { title: "Generate more enquiries", desc: "Clear journeys and calls-to-action that turn visits into conversations." },
    ],
  },
  {
    slug: "web-applications",
    icon: AppWindow,
    name: "Web Applications",
    shortDesc:
      "Scalable and secure web applications built around real business workflows and customer needs.",
    tagline: "Software shaped around your workflow.",
    overview:
      "When off-the-shelf software doesn't fit, a custom web application does. We build secure, scalable applications — portals, dashboards, internal tools and customer-facing platforms — designed around how your business actually works.",
    whatWeBuild: [
      "Customer and client portals",
      "Business dashboards and internal tools",
      "Booking, ordering and workflow systems",
      "SaaS platforms and MVPs",
      "API-driven integrations",
    ],
    capabilities: [
      { title: "Workflow-first design", desc: "We map your real processes before writing code, so the product fits on day one." },
      { title: "Secure architecture", desc: "Authentication, roles and data protection built into the foundation." },
      { title: "Scalable engineering", desc: "A codebase designed to grow from first users to full scale." },
      { title: "Third-party integrations", desc: "Payments, messaging, CRMs and the tools your business already uses." },
      { title: "Role-based experiences", desc: "Different views for admins, teams and customers — each seeing what they need." },
      { title: "Ongoing iteration", desc: "Ship, learn and improve with a partner who stays after launch." },
    ],
    useCases: [
      { title: "Replace spreadsheets", desc: "Turn fragile manual processes into a reliable, shared system." },
      { title: "Serve customers online", desc: "Give customers self-service access to accounts, orders and support." },
      { title: "Unify your operations", desc: "One platform instead of five disconnected tools." },
      { title: "Launch a SaaS product", desc: "Take your software idea from concept to paying customers." },
    ],
  },
  {
    slug: "ai-mvp",
    icon: Sparkles,
    name: "AI Application Development",
    shortDesc:
      "Design and build AI-powered MVPs to validate ideas, solve real problems and reach the market faster.",
    tagline: "Validate your AI idea before you scale it.",
    overview:
      "AI is only valuable when it solves a real problem. We help you identify the right use case, then design and build a focused AI-powered MVP — so you can test with real users, prove value and reach the market faster.",
    whatWeBuild: [
      "AI-powered product MVPs",
      "Chat and assistant experiences",
      "Document and data intelligence tools",
      "AI features for existing products",
      "Rapid prototypes for validation",
    ],
    capabilities: [
      { title: "Use-case discovery", desc: "We help you find where AI genuinely adds value in your business." },
      { title: "Rapid prototyping", desc: "A working MVP in weeks, not months — built for learning fast." },
      { title: "Modern AI models", desc: "Built on leading language and vision models through proven APIs." },
      { title: "Human-centred UX", desc: "AI features designed to feel simple, helpful and trustworthy." },
      { title: "Cost-aware design", desc: "Architectures that keep model usage costs predictable as you grow." },
      { title: "Path to production", desc: "MVPs engineered so a successful test can scale into a full product." },
    ],
    useCases: [
      { title: "Test a startup idea", desc: "Prove demand with a real product before raising or scaling." },
      { title: "Automate knowledge work", desc: "Summaries, drafting and answers from your own documents and data." },
      { title: "Upgrade an existing product", desc: "Add intelligent features that differentiate you from competitors." },
      { title: "Delight customers", desc: "Instant, always-on assistance for the questions your customers ask most." },
    ],
  },
  {
    slug: "mobile-applications",
    icon: Smartphone,
    name: "Mobile Applications",
    shortDesc:
      "Modern mobile experiences for iOS and Android that help businesses engage customers anywhere.",
    tagline: "Your business, in your customer's pocket.",
    overview:
      "Mobile is where your customers spend their time. We design and build polished iOS and Android applications that extend your product, deepen engagement and keep your business one tap away.",
    whatWeBuild: [
      "Customer-facing mobile apps",
      "Companion apps for web platforms",
      "Field and workforce apps",
      "Loyalty and engagement apps",
      "Cross-platform MVPs",
    ],
    capabilities: [
      { title: "iOS and Android", desc: "One codebase, both platforms — without compromising on quality." },
      { title: "Native-feel design", desc: "Interfaces that follow platform conventions and feel instantly familiar." },
      { title: "Offline-ready", desc: "Apps that keep working when connectivity doesn't." },
      { title: "Push & engagement", desc: "Notifications and journeys that bring customers back." },
      { title: "Backend integration", desc: "Seamlessly connected to your existing systems and APIs." },
      { title: "Store launch support", desc: "Guidance through App Store and Google Play submission and release." },
    ],
    useCases: [
      { title: "Extend your web product", desc: "Give your platform a mobile experience customers can carry anywhere." },
      { title: "Empower field teams", desc: "Put data capture and workflows in the hands of people on the move." },
      { title: "Build customer loyalty", desc: "Rewards, reorders and communication in one branded app." },
      { title: "Validate a mobile idea", desc: "Launch a focused MVP and learn from real usage fast." },
    ],
  },
];

export const PRODUCTS = [
  {
    slug: "kryvo-crm",
    name: "Kryvo CRM",
    badge: "SaaS Product",
    headline: "Run your sales. Manage your customers. Grow your business.",
    externalUrl: "https://kryvocrm.com/",
    description:
      "A modern CRM designed to help businesses manage leads, customers, opportunities, tasks and more.",
    overview:
      "Kryvo CRM brings your entire customer journey into one clean workspace. From the first enquiry to a closed deal and beyond, your team always knows who to talk to, what to do next and how the pipeline is performing.",
    mockup: "crm",
    features: [
      { title: "Lead Management", desc: "Capture, qualify and assign leads so no opportunity slips through." },
      { title: "Contacts & Companies", desc: "A complete, organised view of every customer and relationship." },
      { title: "Sales Pipeline", desc: "Visual pipelines that show every opportunity and its stage at a glance." },
      { title: "Tasks & Activities", desc: "Follow-ups, calls and to-dos tracked alongside every deal." },
      { title: "Revenue Tracking", desc: "See deal values, won revenue and forecasts without a spreadsheet." },
      { title: "Reports & Insights", desc: "Clear reporting on pipeline health and team performance." },
    ],
    howItHelps: [
      { title: "Never lose a lead", desc: "Every enquiry is captured and owned, with a clear next step." },
      { title: "Sell with focus", desc: "Your team sees today's priorities instead of digging for them." },
      { title: "One source of truth", desc: "Customer history, notes and deals in one shared place." },
      { title: "Grow with confidence", desc: "Understand what's working and where your revenue comes from." },
    ],
    useCases: [
      { title: "Growing sales teams", desc: "Structure and visibility as your team and pipeline expand." },
      { title: "Service businesses", desc: "Track enquiries, quotes and ongoing client relationships." },
      { title: "Founder-led sales", desc: "A simple system that keeps deals moving while you run the business." },
      { title: "Teams leaving spreadsheets", desc: "All the control, none of the version chaos." },
    ],
  },
  {
    slug: "kryvo-engage",
    name: "Kryvo Engage",
    badge: "SaaS Product",
    headline: "Email, WhatsApp and marketing engagement — built for modern businesses.",
    description:
      "Create campaigns, automate communication and engage your customers across email, WhatsApp and more.",
    overview:
      "Kryvo Engage helps you reach customers where they actually respond. Build beautiful email and WhatsApp campaigns, automate follow-ups and see exactly how every message performs — from one modern dashboard.",
    mockup: "engage",
    features: [
      { title: "Email Campaigns", desc: "Design and send professional campaigns that land in the inbox." },
      { title: "WhatsApp Campaigns", desc: "Reach customers on the channel they check first." },
      { title: "Automation", desc: "Welcome journeys, follow-ups and reminders that run themselves." },
      { title: "Audience Segments", desc: "Target the right customers with the right message." },
      { title: "Campaign Analytics", desc: "Opens, clicks and engagement for every campaign you send." },
      { title: "Engagement Tracking", desc: "Understand which customers are active, warm or slipping away." },
    ],
    howItHelps: [
      { title: "Reach customers anywhere", desc: "Email and WhatsApp together, managed in one place." },
      { title: "Save hours every week", desc: "Automations handle routine communication for you." },
      { title: "Send with confidence", desc: "See what resonates and improve every campaign." },
      { title: "Keep customers close", desc: "Consistent, timely communication that builds loyalty." },
    ],
    useCases: [
      { title: "Marketing teams", desc: "Plan, send and measure multi-channel campaigns without juggling tools." },
      { title: "E-commerce and retail", desc: "Promotions, announcements and re-engagement that drive repeat sales." },
      { title: "Service businesses", desc: "Appointment reminders, follow-ups and seasonal campaigns." },
      { title: "Growing customer bases", desc: "Stay personal with every customer, even as you scale." },
    ],
  },
];

export const PROJECT_TYPES = [
  "Website",
  "Web Application",
  "AI MVP",
  "Mobile Application",
  "Kryvo CRM",
  "Kryvo Engage",
  "Other",
];

export const MARQUEE_ITEMS = [
  "Website Development",
  "Web Applications",
  "AI Application Development",
  "Mobile Applications",
  "Kryvo CRM",
  "Kryvo Engage",
];
